import { DateTime } from 'luxon';
import type { Suggester } from 'redux/modules/suggestions.types';
import type { Currency } from 'types/account';
import type {
  Suggestion,
  SuggestionAsCard,
  SuggestionToHourDraft,
  SuggestionWithProject,
} from 'types/suggestion';
import type { MemoryGroup } from 'types/timeline';
import { isContrastReadable } from 'utils/colors';
import { fromISO } from 'utils/luxon-helpers';
import { getProjectColor } from 'utils/project';
import { combinedDurationOfMemories } from './timeline';

interface CardOptions {
  minHeightInPx?: number;
  isEditing?: boolean;
  // isSelected?: boolean,
  currency: Currency;
}

export function suggestionAsCardFactory(
  suggestion: SuggestionWithProject<Suggester>,
  memoryGroups: Array<MemoryGroup>,
  options: CardOptions
): SuggestionAsCard {
  const {
    isEditing = false,
    // isSelected = false,
    currency,
  } = options;
  const hasProject = !!suggestion.project.base.id;

  // We can't trust the duration sent from the server, it seems
  let realDuration = combinedDurationOfMemories(
    memoryGroups.flatMap(memoryGroup => memoryGroup.memories)
  );

  // If entries are not available:
  if (memoryGroups.length < 1) {
    realDuration = suggestion.duration;
  }

  const numSeconds = realDuration.total_seconds || 0;
  const projectColor = `#${getProjectColor(suggestion.project.base)}` || 'rgba(0,0,0,.06)'; // @todo this better

  // Don't show Timely as suggester, we only care about suggestions via @mention
  const suggester = suggestion.owner.name !== 'Timely' ? suggestion.owner : null;
  const numPlannedSeconds = suggestion.estimated_duration
    ? suggestion.estimated_duration.total_seconds
    : 0;
  const showLogged = numSeconds > 0 || !numPlannedSeconds;
  const hasPlannedTime = !!numPlannedSeconds;

  return {
    type: 'suggestion',
    id: suggestion.id,
    note: suggestion.note || '',
    day: fromISO(suggestion.date).toISODate(),
    // While not technically "logged hours", I'm re-using the HourAsCard structure
    // to allow re-use of <Hour> component
    numLoggedSecondsExact: numSeconds,
    numPlannedSeconds,
    numLoggedMoney: 0,
    loggedMinutes: realDuration.total_minutes,
    plannedMinutes: realDuration.total_minutes,
    projectId: suggestion.project.base.id,
    projectName: suggestion.project.base.name,
    projectColor,
    projectCurrency: suggestion.project.fullProject?.currency ?? currency,
    entryIds: suggestion.suggested_entry_ids,
    memoryGroups,
    cacheBuster: JSON.stringify({
      created: suggestion.created_at,
      isEditing,
      numSeconds,
      projectId: suggestion.project.base.id,
    }), // expand on this as needed, will allow re-render of <Hour />
    clientId: hasProject ? suggestion.project.base.id : -1,
    clientName: hasProject ? suggestion.project.base.client.name : '',
    suggester,
    ui: {
      isSuggestion: true,
      currency,
      isEditing,
      // isSelected,
      hasNoNote: !suggestion.note,
      isContrastReadable: isContrastReadable('#fff', projectColor),
      hasPlannedTime,
      showLogged,
    },
  };
}

export function suggestionToHourDraftFactory(
  suggestion: Suggestion<number>
): SuggestionToHourDraft {
  return {
    suggestion_id: suggestion.id,
    suggester_id: suggestion.owner,
    day: suggestion.date,
    entry_ids: suggestion.suggested_entry_ids,
    duration: suggestion.duration,
    estimated_duration: suggestion.estimated_duration ? suggestion.estimated_duration : undefined,
    project: suggestion.project_id,
    note: suggestion.note || '',
    source: suggestion.source,
    updated_at: suggestion.updated_at
      ? DateTime.fromISO(suggestion.updated_at).toSeconds()
      : DateTime.now().toSeconds(),
    label_ids: suggestion.label_ids,
  };
}
